/**
 * Oath Power Inventory Service
 * API calls for power inventory operations using Oath authentication
 * Routes to /oath/powerinventory/* endpoints
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

/**
 * Get inventory summary for an agency (main + sub-agencies)
 * @param {number} agencyId
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getInventorySummary = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinventory/${agencyId}/summary`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || `Error ${response.status}` };
    }
    const data = await parseResponse(response);
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get unexecuted powers for an agency
 * @param {number} agencyId
 * @param {Object} [options]
 * @param {string} [options.powerTypeCd] - Filter by power type (S or F)
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getUnexecutedPowers = async (agencyId, { powerTypeCd } = {}) => {
  try {
    const params = new URLSearchParams();
    if (powerTypeCd) params.set('powerTypeCd', powerTypeCd);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await authFetch(`${BASE_URL}oath/powerinventory/${agencyId}/unexecuted${query}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || `Error ${response.status}` };
    }
    const data = await parseResponse(response);
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get sub-agencies for dropdown
 * @param {number} agencyId
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getSubAgencies = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinventory/${agencyId}/sub-agencies`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || `Error ${response.status}` };
    }
    const data = await parseResponse(response);
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Reassign powers between agencies
 * @param {Object} request
 * @param {number} request.sourceAgencyId
 * @param {number} request.destinationAgencyId
 * @param {string[]} request.powerNumbers
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const reassignPowers = async ({ sourceAgencyId, destinationAgencyId, powerNumbers }) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/powerinventory/reassign`, {
      method: 'POST',
      body: JSON.stringify({ sourceAgencyId, destinationAgencyId, powerNumbers }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || `Error ${response.status}` };
    }
    const data = await parseResponse(response);
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
